import { WhiteLabelProvider } from "./contexts/WhiteLabelContext";
import React from "react";
import {
  BrowserRouter,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { SentryRoutes } from "./instrument";
import { HelmetProvider } from "./components/metadata/Helmet";
import { AnimatePresence } from "framer-motion";
import PageTransition from "components/PageTransition";

import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import Footer from "components/ui/Footer";
import SEOHead from "./components/ui/SEOHead";
// Profile details are captured in the course completion flow before certificate generation.
import { AuthProvider } from "./hooks/useAuth";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import ProtectedInstructorRoute from "./components/ProtectedInstructorRoute";
import ProtectedCourseAuthorRoute from "./components/ProtectedCourseAuthorRoute";
import NotFound from "pages/NotFound";
import GlobalCart from "./components/cart/GlobalCart";
import { CartProvider } from "./contexts/CartContext";
import { ViewAsProvider } from "./contexts/ViewAsContext";
import { TeamSupportProvider } from "./contexts/TeamSupportContext";
import { ProCertThemeProvider } from "./contexts/ProCertThemeContext";
import ImpersonationBanner from "./components/ImpersonationBanner";
import GlobalNotificationToasts from "./components/GlobalNotificationToasts";
import { getCanonicalLearnerPath } from "./utils/learnerDashboard";

// ---------------------------------------------------------------------------
// Lazy import with retry — handles stale chunks after deploys by cache-busting
// ---------------------------------------------------------------------------
function lazyRetry(importFn) {
  return React.lazy(() =>
    importFn().catch(() => {
      // Retry once with cache-busted URL
      return importFn().catch(() => {
        // Force full reload if retry also fails (new deploy, all chunks stale)
        const reloaded = sessionStorage.getItem("chunk_full_reload");
        if (!reloaded) {
          sessionStorage.setItem("chunk_full_reload", "1");
          window.location.reload();
        } else {
          sessionStorage.removeItem("chunk_full_reload");
        }
        return { default: () => null };
      });
    }),
  );
}

const GroqChatbot = lazyRetry(() => import("./components/ui/GroqChatbot"));

// ---------------------------------------------------------------------------
// React.lazy() declarations for all page components
// ---------------------------------------------------------------------------

// Marketing pages
const HomepageProfessionalLearningPlatform = lazyRetry(
  () => import("./pages/marketing/home"),
);
const ProCertHome = lazyRetry(() => import("./pages/marketing/procert-home"));
const ProCertCourses = lazyRetry(
  () => import("./pages/marketing/procert-home/Courses"),
);
const ProCertFrameworks = lazyRetry(
  () => import("./pages/marketing/procert-home/Frameworks"),
);
const ProCertFAQ = lazyRetry(
  () => import("./pages/marketing/procert-home/FAQ"),
);
const ISOCertificationPathways = lazyRetry(
  () => import("./pages/marketing/certifications"),
);
const FrameworkCatalogPage = lazyRetry(
  () => import("./pages/marketing/frameworks"),
);
const FrameworkCourseDetailsPage = lazyRetry(
  () => import("./pages/marketing/frameworks/detail"),
);
const ISOStandardsGuide = lazyRetry(
  () => import("./pages/marketing/standards-guide"),
);
const ExternalLMSTransitionBridgePage = lazyRetry(
  () => import("./pages/marketing/lms-transition"),
);
const AboutUs = lazyRetry(() => import("./pages/marketing/about-us"));
const ContactPage = lazyRetry(() => import("./pages/marketing/contact"));

// Content pages
const BlogContentHub = lazyRetry(() => import("./pages/content/blogs/hub"));
const BlogPage = lazyRetry(() => import("./pages/content/blogs/BlogPage"));
const SEOArticlePages = lazyRetry(() => import("./pages/content/seo-articles"));
const Downloads = lazyRetry(() => import("./pages/content/downloads"));
const ISO9001ImplementationTemplate = lazyRetry(
  () =>
    import("./pages/content/downloads/guides/ISO9001ImplementationTemplate"),
);
const ISO14001EnvironmentalAspectsRegister = lazyRetry(
  () =>
    import("./pages/content/downloads/guides/ISO14001EnvironmentalAspectsRegister"),
);
const InternalAuditChecklistISO45001 = lazyRetry(
  () =>
    import("./pages/content/downloads/guides/InternalAuditChecklistISO45001"),
);
const RiskManagementGuide = lazyRetry(
  () => import("./pages/content/downloads/guides/RiskManagementGuide"),
);
const IMSWhitePaper = lazyRetry(
  () => import("./pages/content/downloads/guides/IMSWhitePaper"),
);
const ManagementReviewTemplate = lazyRetry(
  () => import("./pages/content/downloads/guides/ManagementReviewTemplate"),
);
const ISO14001ImplementationGuide = lazyRetry(
  () => import("./pages/content/downloads/guides/ISO14001ImplementationGuide"),
);
const ISO45001ImplementationGuide = lazyRetry(
  () => import("./pages/content/downloads/guides/ISO45001ImplementationGuide"),
);
const ISO27001ImplementationGuide = lazyRetry(
  () => import("./pages/content/downloads/guides/ISO27001ImplementationGuide"),
);
const ISO42001ImplementationGuide = lazyRetry(
  () => import("./pages/content/downloads/guides/ISO42001ImplementationGuide"),
);
const ISO50001ImplementationGuide = lazyRetry(
  () => import("./pages/content/downloads/guides/ISO50001ImplementationGuide"),
);
const ISO13485ImplementationGuide = lazyRetry(
  () => import("./pages/content/downloads/guides/ISO13485ImplementationGuide"),
);
const ISO22000ImplementationGuide = lazyRetry(
  () => import("./pages/content/downloads/guides/ISO22000ImplementationGuide"),
);
const CaseStudies = lazyRetry(() => import("./pages/content/case-studies"));
const FAQ = lazyRetry(() => import("./pages/content/faq"));
const TataMotorsISO9001CaseStudy = lazyRetry(
  () => import("./pages/content/case-studies/tata-motors-iso-9001"),
);
const InfosysISO27001CaseStudy = lazyRetry(
  () => import("./pages/content/case-studies/infosys-iso-27001"),
);
const RegionalHospitalNetworkISO45001CaseStudy = lazyRetry(
  () => import("./pages/content/case-studies/hospital-network-iso-45001"),
);
const ConstructionFirmISO45001CaseStudy = lazyRetry(
  () => import("./pages/content/case-studies/construction-iso-45001"),
);
const TechStartupISO27001CaseStudy = lazyRetry(
  () => import("./pages/content/case-studies/tech-startup-iso-27001"),
);
const EnergyCompanyISO14001CaseStudy = lazyRetry(
  () => import("./pages/content/case-studies/energy-company-iso-14001"),
);
const ApolloHospitalsISO45001CaseStudy = lazyRetry(
  () => import("./pages/content/case-studies/apollo-hospitals-iso-45001"),
);
const FAQISO9001Timeline = lazyRetry(
  () => import("./pages/content/faq/iso-9001-timeline"),
);
const FAQISOCosts = lazyRetry(() => import("./pages/content/faq/iso-costs"));

// Legal pages
const CookiePolicyDataCollectionUsageDisclosure = lazyRetry(
  () => import("./pages/legal/cookie-policy"),
);
const PrivacyPolicyDataProtectionCompliance = lazyRetry(
  () => import("./pages/legal/privacy-policy"),
);
const TermsOfServiceUserAgreement = lazyRetry(
  () => import("./pages/legal/terms-of-service"),
);

// Auth pages
const MagicLinkAuthenticationPortal = lazyRetry(
  () => import("./pages/auth/login"),
);
const VerifyEmailPage = lazyRetry(() => import("./pages/auth/verify-email"));
const ResetPasswordPage = lazyRetry(
  () => import("./pages/auth/reset-password"),
);

// User pages
const UserProfileDashboard = lazyRetry(() => import("./pages/user/profile"));
const MyPurchases = lazyRetry(() => import("./pages/user/purchases"));
const CustomerDashboard = lazyRetry(
  () => import("./pages/courses/my-courses/dashboard"),
);
const ProCertMyCourses = lazyRetry(
  () => import("./pages/courses/my-courses/procert"),
);
const MyCourses = lazyRetry(() => import("./pages/courses/my-courses"));

// Admin pages
const AdminCourseManagementDashboard = lazyRetry(
  () => import("./pages/admin/dashboard"),
);
const BlogEditorPage = lazyRetry(
  () => import("./pages/admin/blog-management/BlogEditorPage"),
);
const AdminAnalyticsRevenueDashboard = lazyRetry(
  () => import("./pages/admin/analytics"),
);
const AdminImpersonate = lazyRetry(() => import("./pages/admin/impersonate"));
const BrokerDashboard = lazyRetry(
  () => import("./pages/admin/dashboard/components/BrokerDashboard"),
);
const SuperAdminTransactions = lazyRetry(
  () => import("./pages/admin/analytics/components/SuperAdminTransactions"),
);
const CourseManagementDashboard = lazyRetry(
  () => import("./pages/courses/management"),
);
const CourseCreationEditingInterface = lazyRetry(
  () => import("./pages/courses/creation"),
);
const AICourseStudio = lazyRetry(() => import("./pages/courses/ai-studio"));
const CoursePreview = lazyRetry(() => import("./pages/courses/preview"));

// Course pages
const CoursePage = lazyRetry(() => import("./pages/courses/player"));
const CoursePlayerPreview = lazyRetry(
  () => import("./pages/courses/player-preview"),
);
const CourseCatalog = lazyRetry(() => import("./pages/courses/catalog"));
const CourseDetailPage = lazyRetry(() => import("./pages/courses/detail"));
const CourseCompletion = lazyRetry(() => import("./pages/courses/completion"));

// Bundle pages
const BundlesPage = lazyRetry(() => import("./pages/bundles/BundlesPage"));
const BundleDetailPage = lazyRetry(
  () => import("./pages/bundles/BundleDetailPage"),
);

// Payment pages
const PaymentRedirect = lazyRetry(() => import("./pages/PaymentRedirect"));
const PaymentSuccess = lazyRetry(() => import("./pages/payment-success"));
const PaymentCancelled = lazyRetry(() => import("./pages/payment-cancelled"));

// Partner pages
const PartnerDashboard = lazyRetry(() => import("./pages/partner/dashboard"));
const PartnerOnboarding = lazyRetry(() => import("./pages/partner/onboarding"));
const WorkspaceLayout = lazyRetry(
  () => import("./pages/partner/workspace/WorkspaceLayout"),
);
const TeamStore = lazyRetry(
  () => import("./pages/partner/workspace/TeamStore"),
);

// Instructor and batch pages (already lazy)
const InstructorOnboarding = lazyRetry(
  () => import("./pages/instructor/onboarding"),
);
const InstructorDashboard = lazyRetry(
  () => import("./pages/instructor/dashboard"),
);
const BatchCreatePage = lazyRetry(
  () => import("./pages/instructor/batches/create"),
);
const BatchManagePage = lazyRetry(
  () => import("./pages/instructor/batches/manage"),
);
const BatchSessionDetail = lazyRetry(
  () => import("./pages/instructor/batches/session"),
);
const BatchCatalogPage = lazyRetry(
  () => import("./pages/batches/BatchCatalogPage"),
);
const BatchDetailPage = lazyRetry(
  () => import("./pages/batches/BatchDetailPage"),
);
const LearnerBatchDetail = lazyRetry(
  () => import("./pages/user/batches/index"),
);
const BatchEnrollPage = lazyRetry(
  () => import("./pages/batches/BatchEnrollPage"),
);
const BatchPaymentSuccess = lazyRetry(
  () => import("./pages/batches/BatchPaymentSuccess"),
);
const EmbedPdfPreviewTest = lazyRetry(
  () => import("./pages/dev/pdf-preview-test"),
);

const PARTNER_DOMAIN =
  import.meta.env.VITE_PARTNER_DOMAIN || "procertcompliance.com";

// Shared Suspense fallback
const SuspenseFallback = (
  <div className="min-h-screen bg-dm-page transition-colors duration-300" />
);

// Keep admin links in previously sent partner emails usable.
const PlatformAdminRedirect = () => {
  const { search } = useLocation();

  React.useEffect(() => {
    window.location.replace(`https://skillcompliance.com/admin/dashboard${search}`);
  }, [search]);

  return SuspenseFallback;
};

const AnimatedRoutes = () => {
  const location = useLocation();

  // Detect if we're on a partner subdomain or custom domain
  const hostname = window.location.hostname.toLowerCase();
  const isNativeSubdomain =
    hostname.endsWith(PARTNER_DOMAIN) &&
    hostname !== PARTNER_DOMAIN &&
    hostname !== `www.${PARTNER_DOMAIN}`;

  // For custom domains the edge function injects meta tags
  const metaSlug =
    document.querySelector('meta[name="x-partner-slug"]')?.content || null;

  const isPartnerSubdomain = isNativeSubdomain || !!metaSlug;
  const partnerSlug = isNativeSubdomain
    ? hostname.replace(`.${PARTNER_DOMAIN}`, "")
    : metaSlug;

  // Root domain (ProCertCompliance) handling
  const isProCertDev = import.meta.env.VITE_SHOW_PROCERT_HOME === "true";
  if (
    hostname === PARTNER_DOMAIN ||
    hostname === `www.${PARTNER_DOMAIN}` ||
    (isProCertDev && hostname === "localhost")
  ) {
    return (
      <AnimatePresence mode="wait">
        <SentryRoutes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <ProCertHome />
              </React.Suspense>
            }
          />
          <Route
            path="/courses"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <ProCertCourses />
              </React.Suspense>
            }
          />
          <Route
            path="/courses/:slug/instructor-led"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <CourseDetailPage deliveryMode="instructor_led" />
              </React.Suspense>
            }
          />
          <Route
            path="/courses/:slug/in-person"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <CourseDetailPage deliveryMode="in_person" />
              </React.Suspense>
            }
          />
          <Route
            path="/courses/:slug"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <CourseDetailPage />
              </React.Suspense>
            }
          />
          <Route
            path="/batches"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <BatchCatalogPage />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/batches/:batchSlug"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <BatchDetailPage />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/batches/:batchSlug/enroll"
            element={
              <PageTransition>
                <ProtectedRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <BatchEnrollPage />
                  </React.Suspense>
                </ProtectedRoute>
              </PageTransition>
            }
          />
          <Route
            path="/payment-success"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <PaymentSuccess />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/payment-cancelled"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <PaymentCancelled />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/batch-payment-success"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <BatchPaymentSuccess />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/frameworks"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <ProCertFrameworks />
              </React.Suspense>
            }
          />
          <Route
            path="/faq"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <ProCertFAQ />
              </React.Suspense>
            }
          />
          <Route
            path="/login"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <MagicLinkAuthenticationPortal />
              </React.Suspense>
            }
          />
          <Route
            path="/user/courses"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <CustomerDashboard />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/profile"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <UserProfileDashboard />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/purchases"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <MyPurchases />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-courses"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <ProCertMyCourses />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/learning"
            element={
              <Navigate
                to={getCanonicalLearnerPath("/user/learning")}
                replace
              />
            }
          />
          <Route
            path="/user/batches/:batchId"
            element={
              <PageTransition>
                <ProtectedRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <LearnerBatchDetail />
                  </React.Suspense>
                </ProtectedRoute>
              </PageTransition>
            }
          />

          {/* Course Playing Routes */}
          <Route
            path="/courses/player/:courseId"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <React.Suspense fallback={SuspenseFallback}>
                    <CoursePage />
                  </React.Suspense>
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/player-preview/:courseId"
            element={
              <ProtectedCourseAuthorRoute>
                <PageTransition>
                  <React.Suspense fallback={SuspenseFallback}>
                    <CoursePlayerPreview />
                  </React.Suspense>
                </PageTransition>
              </ProtectedCourseAuthorRoute>
            }
          />
          <Route
            path="/courses/completion/:courseId"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <React.Suspense fallback={SuspenseFallback}>
                    <CourseCompletion />
                  </React.Suspense>
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <PartnerDashboard />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={<PlatformAdminRedirect />}
          />

          {/* Auth routes */}
          <Route
            path="/auth/login"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <MagicLinkAuthenticationPortal />
              </React.Suspense>
            }
          />
          <Route
            path="/auth/verify-email"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <VerifyEmailPage />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/auth/reset-password"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <ResetPasswordPage />
                </React.Suspense>
              </PageTransition>
            }
          />

          {/* Instructor routes */}
          <Route
            path="/instructor/onboarding"
            element={
              <PageTransition>
                <ProtectedRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <InstructorOnboarding />
                  </React.Suspense>
                </ProtectedRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/dashboard"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <InstructorDashboard />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/courses/create"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <CourseCreationEditingInterface />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/courses/edit/:id"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <CourseCreationEditingInterface />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/batches/create"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <BatchCreatePage />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/batches/:batchId"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <BatchManagePage />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/batches/:batchId/session/:sessionId"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <BatchSessionDetail />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />

          {/* Partner Onboarding (self-service application) */}
          <Route
            path="/partner/onboarding"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <React.Suspense fallback={SuspenseFallback}>
                    <PartnerOnboarding />
                  </React.Suspense>
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* Legal Pages */}
          <Route
            path="/cookie-policy"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <CookiePolicyDataCollectionUsageDisclosure />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <PrivacyPolicyDataProtectionCompliance />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/terms-of-service"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <TermsOfServiceUserAgreement />
                </React.Suspense>
              </PageTransition>
            }
          />

          {/* Support links across the learner journey point here. */}
          <Route
            path="/contact-us"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <ContactPage />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="*"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <NotFound />
              </React.Suspense>
            }
          />
        </SentryRoutes>
      </AnimatePresence>
    );
  }

  // If on partner subdomain, render partner store directly
  if (isPartnerSubdomain && partnerSlug && partnerSlug !== "www") {
    return (
      <AnimatePresence mode="wait">
        <SentryRoutes location={location} key={location.pathname}>
          {/* Partner Dashboard - for logged in partner admins */}
          <Route
            path="/admin/dashboard"
            element={<PlatformAdminRedirect />}
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <PartnerDashboard
                    partnerSlug={partnerSlug}
                    isWhiteLabel={true}
                  />
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          {/* Customer Dashboard for partner subdomain */}
          <Route
            path="/user/courses"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <CustomerDashboard />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/profile"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <UserProfileDashboard />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/purchases"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <MyPurchases />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-courses"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <ProCertMyCourses />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/learning"
            element={
              <Navigate
                to={getCanonicalLearnerPath("/user/learning")}
                replace
              />
            }
          />
          <Route
            path="/user/batches/:batchId"
            element={
              <PageTransition>
                <ProtectedRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <LearnerBatchDetail />
                  </React.Suspense>
                </ProtectedRoute>
              </PageTransition>
            }
          />

          {/* Course Playing Routes */}
          <Route
            path="/courses/player/:courseId"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <React.Suspense fallback={SuspenseFallback}>
                    <CoursePage />
                  </React.Suspense>
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/player-preview/:courseId"
            element={
              <ProtectedCourseAuthorRoute>
                <PageTransition>
                  <React.Suspense fallback={SuspenseFallback}>
                    <CoursePlayerPreview />
                  </React.Suspense>
                </PageTransition>
              </ProtectedCourseAuthorRoute>
            }
          />
          <Route
            path="/courses/completion/:courseId"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <React.Suspense fallback={SuspenseFallback}>
                    <CourseCompletion />
                  </React.Suspense>
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* Auth routes for partner subdomain */}
          <Route
            path="/login"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <MagicLinkAuthenticationPortal />
              </React.Suspense>
            }
          />
          <Route
            path="/auth/login"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <MagicLinkAuthenticationPortal />
              </React.Suspense>
            }
          />
          {/* Partner-subdomain signup entry — email invitation links (SCRUM-110)
              land here with ?invite=<token>. Reuse the unified auth portal in
              signup mode so invite handling, OAuth, and email verification all
              work with one codepath. */}
          <Route
            path="/auth/signup"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <MagicLinkAuthenticationPortal />
              </React.Suspense>
            }
          />
          <Route
            path="/auth/verify-email"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <VerifyEmailPage />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/auth/reset-password"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <ResetPasswordPage />
                </React.Suspense>
              </PageTransition>
            }
          />

          {/* Instructor routes on partner subdomain */}
          <Route
            path="/instructor/onboarding"
            element={
              <PageTransition>
                <ProtectedRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <InstructorOnboarding />
                  </React.Suspense>
                </ProtectedRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/dashboard"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <InstructorDashboard />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/courses/create"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <CourseCreationEditingInterface />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/courses/edit/:id"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <CourseCreationEditingInterface />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/batches/create"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <BatchCreatePage />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/batches/:batchId"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <BatchManagePage />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />
          <Route
            path="/instructor/batches/:batchId/session/:sessionId"
            element={
              <PageTransition>
                <ProtectedInstructorRoute>
                  <React.Suspense fallback={SuspenseFallback}>
                    <BatchSessionDetail />
                  </React.Suspense>
                </ProtectedInstructorRoute>
              </PageTransition>
            }
          />

          {/* Batch routes for partner subdomain */}
          <Route
            path="/batches"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <BatchCatalogPage />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/batches/:batchSlug"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <BatchDetailPage />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/batches/:batchSlug/enroll"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <React.Suspense fallback={SuspenseFallback}>
                    <BatchEnrollPage />
                  </React.Suspense>
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* Payment result routes — Stripe redirects land here after checkout.
              Without these, /payment-success falls through to TeamStore and the
              frontend fallback enrollment never runs. */}
          <Route
            path="/payment-success"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <PaymentSuccess />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/payment-cancelled"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <PaymentCancelled />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/batch-payment-success"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <BatchPaymentSuccess />
                </React.Suspense>
              </PageTransition>
            }
          />

          {/* Course Details Page for partner subdomain */}
          <Route
            path="/courses/:slug/instructor-led"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <CourseDetailPage deliveryMode="instructor_led" />
              </React.Suspense>
            }
          />
          <Route
            path="/courses/:slug/in-person"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <CourseDetailPage deliveryMode="in_person" />
              </React.Suspense>
            }
          />
          <Route
            path="/courses/:slug"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <CourseDetailPage />
              </React.Suspense>
            }
          />

          {/* FAQ */}
          <Route
            path="/faq"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <ProCertFAQ />
              </React.Suspense>
            }
          />

          {/* Contact */}
          <Route
            path="/contact-us"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <ContactPage />
                </React.Suspense>
              </PageTransition>
            }
          />

          {/* Legal Pages */}
          <Route
            path="/cookie-policy"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <CookiePolicyDataCollectionUsageDisclosure />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <PrivacyPolicyDataProtectionCompliance />
                </React.Suspense>
              </PageTransition>
            }
          />
          <Route
            path="/terms-of-service"
            element={
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <TermsOfServiceUserAgreement />
                </React.Suspense>
              </PageTransition>
            }
          />

          {["/courses", "/course-catalog"].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <Navigate
                  to={{ pathname: "/", search: location.search, hash: location.hash }}
                  replace
                />
              }
            />
          ))}

          {/* Partner store home and branded not-found page */}
          <Route
            path="*"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <WorkspaceLayout partnerSlug={partnerSlug} />
              </React.Suspense>
            }
          >
            <Route
              index
              element={
                <React.Suspense fallback={SuspenseFallback}>
                  <TeamStore />
                </React.Suspense>
              }
            />
            <Route
              path="store"
              element={
                <React.Suspense fallback={SuspenseFallback}>
                  <TeamStore />
                </React.Suspense>
              }
            />
            <Route
              path="*"
              element={
                <React.Suspense fallback={SuspenseFallback}>
                  <NotFound />
                </React.Suspense>
              }
            />
          </Route>
        </SentryRoutes>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <SentryRoutes location={location} key={location.pathname}>
        {/* Define your route here */}
        <Route
          path="/"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <HomepageProfessionalLearningPlatform />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/home"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <HomepageProfessionalLearningPlatform />
              </React.Suspense>
            </PageTransition>
          }
        />
        {import.meta.env.DEV && (
          <Route
            path="/dev/pdf-preview-test"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <EmbedPdfPreviewTest />
              </React.Suspense>
            }
          />
        )}

        <Route
          path="/iso-certification-pathways"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISOCertificationPathways />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/certifications/:frameworkId"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <FrameworkCatalogPage />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/certifications/:frameworkId/course/:courseId"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <FrameworkCourseDetailsPage />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/blogs"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <BlogContentHub />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/blogs/:slug"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <BlogPage />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/about-us"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <AboutUs />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/contact-us"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ContactPage />
              </React.Suspense>
            </PageTransition>
          }
        />

        {/* Partner Onboarding */}
        <Route
          path="/partner/onboarding"
          element={
            <ProtectedRoute>
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <PartnerOnboarding />
                </React.Suspense>
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* Authentication Routes */}
        <Route
          path="/login"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <MagicLinkAuthenticationPortal />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/auth/login"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <MagicLinkAuthenticationPortal />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/auth/signup"
          element={<Navigate to="/auth/login?mode=signup" replace />}
        />
        <Route
          path="/auth/verify-email"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <VerifyEmailPage />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/auth/reset-password"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ResetPasswordPage />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ResetPasswordPage />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/user/profile"
          element={
            <PageTransition>
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <UserProfileDashboard />
                </React.Suspense>
              </ProtectedRoute>
            </PageTransition>
          }
        />
        <Route
          path="/user/purchases"
          element={
            <PageTransition>
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <MyPurchases />
                </React.Suspense>
              </ProtectedRoute>
            </PageTransition>
          }
        />
        <Route
          path="/user/learning"
          element={
            <Navigate to={getCanonicalLearnerPath("/user/learning")} replace />
          }
        />
        <Route
          path="/user/batches/:batchId"
          element={
            <PageTransition>
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <LearnerBatchDetail />
                </React.Suspense>
              </ProtectedRoute>
            </PageTransition>
          }
        />
        <Route
          path="/user/courses"
          element={
            <PageTransition>
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <CustomerDashboard />
                </React.Suspense>
              </ProtectedRoute>
            </PageTransition>
          }
        />
        <Route
          path="/partner/dashboard"
          element={
            <PageTransition>
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <PartnerDashboard />
                </React.Suspense>
              </ProtectedRoute>
            </PageTransition>
          }
        />

        {/* Admin Course Management Routes */}
        <Route
          path="/admin/dashboard"
          element={
            <PageTransition>
              <ProtectedAdminRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <AdminCourseManagementDashboard />
                </React.Suspense>
              </ProtectedAdminRoute>
            </PageTransition>
          }
        />
        <Route
          path="/admin/blog/create"
          element={
            <PageTransition>
              <ProtectedAdminRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <BlogEditorPage />
                </React.Suspense>
              </ProtectedAdminRoute>
            </PageTransition>
          }
        />
        <Route
          path="/admin/blog/edit/:id"
          element={
            <PageTransition>
              <ProtectedAdminRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <BlogEditorPage />
                </React.Suspense>
              </ProtectedAdminRoute>
            </PageTransition>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <PageTransition>
              <ProtectedAdminRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <AdminAnalyticsRevenueDashboard />
                </React.Suspense>
              </ProtectedAdminRoute>
            </PageTransition>
          }
        />
        <Route
          path="/admin/broker-dashboard"
          element={
            <PageTransition>
              <ProtectedAdminRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <BrokerDashboard />
                </React.Suspense>
              </ProtectedAdminRoute>
            </PageTransition>
          }
        />
        {/* Admin Impersonation - signs in as target user, no admin guard needed (token-based) */}
        <Route
          path="/admin/impersonate"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <AdminImpersonate />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/admin/transactions"
          element={
            <PageTransition>
              <ProtectedAdminRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <SuperAdminTransactions />
                </React.Suspense>
              </ProtectedAdminRoute>
            </PageTransition>
          }
        />
        <Route
          path="/courses/management"
          element={
            <PageTransition>
              <ProtectedAdminRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <CourseManagementDashboard />
                </React.Suspense>
              </ProtectedAdminRoute>
            </PageTransition>
          }
        />
        <Route
          path="/course-creation"
          element={<Navigate to="/courses/create" replace />}
        />
        <Route
          path="/courses/create"
          element={
            <PageTransition>
              <ProtectedAdminRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <CourseCreationEditingInterface />
                </React.Suspense>
              </ProtectedAdminRoute>
            </PageTransition>
          }
        />
        <Route
          path="/courses/edit/:id"
          element={
            <PageTransition>
              <ProtectedAdminRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <CourseCreationEditingInterface />
                </React.Suspense>
              </ProtectedAdminRoute>
            </PageTransition>
          }
        />
        <Route
          path="/courses/:courseId/ai-studio"
          element={
            <PageTransition>
              <ProtectedAdminRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <AICourseStudio />
                </React.Suspense>
              </ProtectedAdminRoute>
            </PageTransition>
          }
        />
        <Route
          path="/courses/preview/:id"
          element={
            <ProtectedCourseAuthorRoute>
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <CoursePreview />
                </React.Suspense>
              </PageTransition>
            </ProtectedCourseAuthorRoute>
          }
        />
        <Route
          path="/courses/player/:courseId"
          element={
            <ProtectedRoute>
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <CoursePage />
                </React.Suspense>
              </PageTransition>
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/player-preview/:courseId"
          element={
            <ProtectedCourseAuthorRoute>
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <CoursePlayerPreview />
                </React.Suspense>
              </PageTransition>
            </ProtectedCourseAuthorRoute>
          }
        />
        <Route
          path="/my-courses"
          element={
            <PageTransition>
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <MyCourses />
                </React.Suspense>
              </ProtectedRoute>
            </PageTransition>
          }
        />
        <Route
          path="/courses/completion/:courseId"
          element={
            <ProtectedRoute>
              <PageTransition>
                <React.Suspense fallback={SuspenseFallback}>
                  <CourseCompletion />
                </React.Suspense>
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* SEO Article Pages - Dynamic routing for comprehensive articles */}
        <Route
          path="/articles"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <SEOArticlePages />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/articles/:id"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <SEOArticlePages />
              </React.Suspense>
            </PageTransition>
          }
        />

        {/* Individual Case Study Pages - Detailed case study routes */}
        <Route
          path="/case-studies/tata-motors-iso-9001"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <TataMotorsISO9001CaseStudy />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/case-studies/infosys-iso-27001"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <InfosysISO27001CaseStudy />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/case-studies/apollo-hospitals-iso-45001"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ApolloHospitalsISO45001CaseStudy />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/faq/iso-9001-timeline"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <FAQISO9001Timeline />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/faq/iso-costs"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <FAQISOCosts />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/case-studies/hospital-network-iso-45001"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <RegionalHospitalNetworkISO45001CaseStudy />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/case-studies/construction-iso-45001"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ConstructionFirmISO45001CaseStudy />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/case-studies/tech-startup-iso-27001"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <TechStartupISO27001CaseStudy />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/case-studies/energy-company-iso-14001"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <EnergyCompanyISO14001CaseStudy />
              </React.Suspense>
            </PageTransition>
          }
        />

        {/* External LMS Transition Bridge Page */}
        <Route
          path="/lms-transition"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ExternalLMSTransitionBridgePage />
              </React.Suspense>
            </PageTransition>
          }
        />

        {/* Legal Pages */}
        <Route
          path="/cookie-policy"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <CookiePolicyDataCollectionUsageDisclosure />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <PrivacyPolicyDataProtectionCompliance />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/terms-of-service"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <TermsOfServiceUserAgreement />
              </React.Suspense>
            </PageTransition>
          }
        />

        {/* Other Pages */}
        <Route
          path="/resources"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <Downloads />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/iso-9001-implementation-template"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISO9001ImplementationTemplate />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/iso-9001-implementation-guide"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISO9001ImplementationTemplate />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/iso-14001-environmental-aspects-register"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISO14001EnvironmentalAspectsRegister />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/iso-14001-implementation-guide"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISO14001ImplementationGuide />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/iso-45001-implementation-guide"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISO45001ImplementationGuide />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/iso-27001-implementation-guide"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISO27001ImplementationGuide />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/iso-42001-implementation-guide"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISO42001ImplementationGuide />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/iso-50001-implementation-guide"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISO50001ImplementationGuide />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/iso-13485-implementation-guide"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISO13485ImplementationGuide />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/iso-22000-implementation-guide"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISO22000ImplementationGuide />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/internal-audit-checklist-iso-45001"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <InternalAuditChecklistISO45001 />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/risk-management-implementation-guide"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <RiskManagementGuide />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/integrated-management-system-white-paper"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <IMSWhitePaper />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/resources/management-review-meeting-template"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ManagementReviewTemplate />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/case-studies"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <CaseStudies />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/faq"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <FAQ />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/iso-standards-guide"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <ISOStandardsGuide />
              </React.Suspense>
            </PageTransition>
          }
        />

        {/* Enhanced Course Catalog */}
        <Route
          path="/courses"
          element={<Navigate to="/course-catalog" replace />}
        />
        <Route
          path="/course-catalog"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <CourseCatalog />
              </React.Suspense>
            </PageTransition>
          }
        />
        {/* Delivery-mode variants — also reachable from white-label store
            previews served on the main domain at /w/:slug/store */}
        <Route
          path="/courses/:slug/instructor-led"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <CourseDetailPage deliveryMode="instructor_led" />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/courses/:slug/in-person"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <CourseDetailPage deliveryMode="in_person" />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/courses/:slug"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <CourseDetailPage />
              </React.Suspense>
            </PageTransition>
          }
        />

        {/* Bundles */}
        <Route
          path="/bundles"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <BundlesPage />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/bundles/:bundleId"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <BundleDetailPage />
              </React.Suspense>
            </PageTransition>
          }
        />

        {/* Payment Redirects */}
        <Route
          path="/razorpay-demo/payment-redirect"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <PaymentRedirect />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/payment-success"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <PaymentSuccess />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/payment-cancelled"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <PaymentCancelled />
              </React.Suspense>
            </PageTransition>
          }
        />

        {/* Batch Training Catalog (public) */}
        <Route
          path="/batches"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <BatchCatalogPage />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/batches/:batchSlug"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <BatchDetailPage />
              </React.Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/batches/:batchSlug/enroll"
          element={
            <PageTransition>
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <BatchEnrollPage />
                </React.Suspense>
              </ProtectedRoute>
            </PageTransition>
          }
        />
        <Route
          path="/batch-payment-success"
          element={
            <PageTransition>
              <React.Suspense fallback={SuspenseFallback}>
                <BatchPaymentSuccess />
              </React.Suspense>
            </PageTransition>
          }
        />

        {/* Instructor Onboarding (from partner invite) */}
        <Route
          path="/instructor/onboarding"
          element={
            <PageTransition>
              <ProtectedRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <InstructorOnboarding />
                </React.Suspense>
              </ProtectedRoute>
            </PageTransition>
          }
        />

        {/* Instructor Dashboard Routes */}
        <Route
          path="/instructor/dashboard"
          element={
            <PageTransition>
              <ProtectedInstructorRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <InstructorDashboard />
                </React.Suspense>
              </ProtectedInstructorRoute>
            </PageTransition>
          }
        />
        <Route
          path="/instructor/courses/create"
          element={
            <PageTransition>
              <ProtectedInstructorRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <CourseCreationEditingInterface />
                </React.Suspense>
              </ProtectedInstructorRoute>
            </PageTransition>
          }
        />
        <Route
          path="/instructor/courses/edit/:id"
          element={
            <PageTransition>
              <ProtectedInstructorRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <CourseCreationEditingInterface />
                </React.Suspense>
              </ProtectedInstructorRoute>
            </PageTransition>
          }
        />
        <Route
          path="/instructor/batches/create"
          element={
            <PageTransition>
              <ProtectedInstructorRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <BatchCreatePage />
                </React.Suspense>
              </ProtectedInstructorRoute>
            </PageTransition>
          }
        />
        <Route
          path="/instructor/batches/:batchId"
          element={
            <PageTransition>
              <ProtectedInstructorRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <BatchManagePage />
                </React.Suspense>
              </ProtectedInstructorRoute>
            </PageTransition>
          }
        />
        <Route
          path="/instructor/batches/:batchId/session/:sessionId"
          element={
            <PageTransition>
              <ProtectedInstructorRoute>
                <React.Suspense fallback={SuspenseFallback}>
                  <BatchSessionDetail />
                </React.Suspense>
              </ProtectedInstructorRoute>
            </PageTransition>
          }
        />

        {/* Workspace Routes */}
        <Route
          path="/w/:slug"
          element={
            <React.Suspense fallback={SuspenseFallback}>
              <WorkspaceLayout />
            </React.Suspense>
          }
        >
          <Route
            path="store"
            element={
              <React.Suspense fallback={SuspenseFallback}>
                <TeamStore />
              </React.Suspense>
            }
          />
          {/* Add other workspace routes here, e.g., dashboard, courses */}
        </Route>

        {/* 404 Not Found - Must be last */}
        <Route
          path="*"
          element={
            <PageTransition>
              <NotFound />
            </PageTransition>
          }
        />
      </SentryRoutes>
    </AnimatePresence>
  );
};

const Routes = () => {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <WhiteLabelProvider>
        <AuthProvider>
          <ViewAsProvider>
            <CartProvider>
              <ProCertThemeProvider>
                <TeamSupportProvider>
                  <ErrorBoundary>
                    <ScrollToTop />
                    <SEOHead priority={0} />
                    <ImpersonationBanner />
                    <GlobalNotificationToasts />
                    <VisibleChatbot />
                    <VisibleCart />
                    <AnimatedRoutes />
                    <RoutesFooter />
                  </ErrorBoundary>
                </TeamSupportProvider>
              </ProCertThemeProvider>
            </CartProvider>
          </ViewAsProvider>
        </AuthProvider>
        </WhiteLabelProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
};

// Helper component that uses react-router location to decide footer visibility
const RoutesFooter = () => {
  const location = useLocation();
  const path = location?.pathname || "";
  const hideFooterOn = ["/courses/player", "/courses/player-preview"];
  const shouldHide =
    hideFooterOn.some((p) => path.startsWith(p)) || path.endsWith("/ai-studio");

  // Hide global footer on ProCert root domain (it uses its own ProCertFooter)
  const hostname = window.location.hostname.toLowerCase();
  const isProCertDev = import.meta.env.VITE_SHOW_PROCERT_HOME === "true";
  const isProCertDomain =
    hostname === PARTNER_DOMAIN ||
    hostname === `www.${PARTNER_DOMAIN}` ||
    (isProCertDev && hostname === "localhost");

  return shouldHide || isProCertDomain ? null : <Footer />;
};

const VisibleChatbot = () => {
  const location = useLocation();
  const path = location?.pathname || "";
  const hideChatbotOn = [
    "/courses/player",
    "/courses/player-preview",
    "/login",
    "/auth/login",
  ];
  const shouldHide = hideChatbotOn.some((p) => path.startsWith(p));
  return shouldHide ? null : (
    <React.Suspense fallback={null}>
      <GroqChatbot />
    </React.Suspense>
  );
};

const VisibleCart = () => {
  const location = useLocation();
  const path = location?.pathname || "";
  const hideCartOn = [
    "/login",
    "/auth/login",
    "/courses/player",
    "/courses/player-preview",
  ];
  const shouldHide = hideCartOn.some((p) => path.startsWith(p));
  return shouldHide ? null : <GlobalCart />;
};

export default Routes;
